import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Link,
  OutlinedInput,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { FC, JSXElementConstructor, ReactElement, useMemo, useState } from "react";
import { GridColDef, GridRowParams, GridActionsCellItem, GridDeleteIcon } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import FormInputText from "../../../../Components/Forms/FormInputText/form-input-text";
import FormSection from "../../../../Components/Forms/FormSection/form-section";
import { produce } from "immer";
import InfoIcon from "@mui/icons-material/Info";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { v4 as uuidv4 } from "uuid";
import { DeleteSection } from "../../../../Redux/Reducers/redux-slice-form-validation";
import { useDispatch } from "react-redux";
import { DbMetadataViewInfos } from "../../../../Core/Api/Types/db-metadata-view-infos";
import { DbMetadataViewGroup } from "../../../../Core/Api/Types/db-metadata-view-group";
import DragOrderedChildren from "../../../../Components/Misc/DragOrderedChildren/drag-ordered-children";
import { cacheService } from "../../../../Core/Api/services";
import { DbMetadataViewGroupColumn } from "../../../../Core/Api/Types/db-metadata-view-group-column";
import { DbMetadataViewGroupRow } from "../../../../Core/Api/Types/db-metadata-view-group-row";
import { NewMetadataIcon } from "../../Inventory/InventoryAssetSingle/AssetSingleMetadata/asset-single-metadata";
import AppsIcon from "@mui/icons-material/Apps";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { useTranslation } from "react-i18next";

export type EditMetadataViewGroupsProps = {
  formId: string;
  newMetadataView: DbMetadataViewInfos;
  setNewMetadataView: React.Dispatch<React.SetStateAction<DbMetadataViewInfos | null>>;
};

const SELECTABLE_ICON_LIST = [
  "personal_video",
  "play_arrow",
  "equalizer",
  "graphic_eq",
  "info",
  "api",
  "arrow_drop_down",
  "arrow_drop_up",
  "assistant_photo",
  "audio_file",
  "autorenew",
  "cancel",
  "check",
  "check_circle",
  "cloud_queue",
  "code",
  "connected_tv",
  "contact_page",
  "contrast",
  "create",
  "crop_free",
  "delete",
  "email",
  "event",
  "file_download",
  "flag",
  "folder",
  "forum",
  "grid_view",
  "headphones",
  "history",
  "hourglass_bottom",
  "image",
  "laptop",
  "library_music",
  "place",
  "zmdi zmdi-archive m-r-5",
  "zmdi zmdi-comment-text m-r-5",
  "zmdi zmdi-comments m-r-5",
  "zmdi zmdi-flag m-r-5",
  "zmdi zmdi-translate",
  "zmdi zmdi-image m-r-5",
  "zmdi zmdi-file-text",
  "zmdi zmdi-book-image m-r-5",
  "zmdi zmdi-collection-video m-r-5",
  "zmdi zmdi-folder m-r-5",
  "zmdi zmdi-time m-r-5",
  "zmdi zmdi-collection-text m-r-5",
  "zmdi zmdi-local-movies m-r-5",
  "zmdi zmdi-speaker m-r-5",
  "zmdi zmdi-globe-alt m-r-5",
  "zmdi zmdi-wrench m-r-5",
  "zmdi zmdi-movie m-r-5",
];

const EditMetadataViewGroups: FC<EditMetadataViewGroupsProps> = (props) => {
  const { t } = useTranslation();
  const { newMetadataView, setNewMetadataView, formId } = props;

  const [selectedMetadataViewGroupsRow, setSelectedMetadataViewGroupsRow] = useState<number | null>(0);
  const [selectedMetadata, setSelectedMetadata] = useState<string | undefined>(undefined);
  const [filterIcon, setFilterIcon] = useState("");
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const dispatch = useDispatch();
  const theme = useTheme();

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const metadataCols: GridColDef[] = [
    {
      field: "id",
      headerName: t("table:headerName.metadataId"),
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
    },
    {
      field: "metadataName",
      headerName: t("table:headerName.metadataName"),
      flex: CellSize.small,
      minWidth: CellSize.small,
    },
    {
      field: "label",
      headerName: t("table:headerName.metadataLabel"),
      flex: CellSize.small,
      minWidth: CellSize.small,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        let actions: any[] = [];
        if (metadataRows.findIndex((x) => x.id === (params.id as number)) > 0) {
          actions.push(
            <GridActionsCellItem
              icon={<ArrowUpwardIcon color="action" />}
              label={t("common:moveUp")}
              onClick={() => {
                setNewMetadataView(
                  produce((draft) => {
                    if (
                      selectedMetadataViewGroupsRow === null ||
                      !draft ||
                      !draft.groups ||
                      !draft.groups[selectedMetadataViewGroupsRow] ||
                      !draft.groups[selectedMetadataViewGroupsRow].rows
                    )
                      return;
                    const columnIndex = draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns!.findIndex(
                      (x) => x!.metadataFieldIds && x!.metadataFieldIds[0] === (params.id as number)
                    );
                    if (columnIndex < 0) return;
                    const tempMeta = {
                      ...draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns![columnIndex - 1],
                    };
                    draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns![columnIndex - 1] = {
                      ...draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns![columnIndex],
                    };
                    draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns![columnIndex] = {
                      metadataFieldIds: tempMeta.metadataFieldIds || [],
                      metadataFields: tempMeta.metadataFields || [],
                    };

                    draft!.groups = [...draft.groups]; // Trigger updates
                  })
                );
              }}
            />
          );
        }
        if (
          metadataRows &&
          metadataRows.findIndex((x) => x.id === (params.id as number)) < metadataRows.length - 1 &&
          metadataRows.findIndex((x) => x.id === (params.id as number)) >= 0
        ) {
          actions.push(
            <GridActionsCellItem
              icon={<ArrowDownwardIcon color="action" />}
              label={t("common:moveDown")}
              onClick={() => {
                setNewMetadataView(
                  produce((draft) => {
                    if (
                      selectedMetadataViewGroupsRow === null ||
                      !draft ||
                      !draft.groups ||
                      !draft.groups[selectedMetadataViewGroupsRow] ||
                      !draft.groups[selectedMetadataViewGroupsRow].rows
                    )
                      return;
                    const columnIndex = draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns!.findIndex(
                      (x) => x!.metadataFieldIds && x!.metadataFieldIds[0] === (params.id as number)
                    );
                    if (columnIndex < 0) return;
                    const tempMeta = {
                      ...draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns![columnIndex + 1],
                    };
                    draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns![columnIndex + 1] = {
                      ...draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns![columnIndex],
                    };
                    draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns![columnIndex] = {
                      metadataFieldIds: tempMeta.metadataFieldIds || [],
                      metadataFields: tempMeta.metadataFields || [],
                    };

                    draft!.groups = [...draft.groups]; // Trigger updates
                  })
                );
              }}
            />
          );
        }
        actions.push(
          <GridActionsCellItem
            icon={
              <Tooltip title={t("tooltip:title.removeMetadata")}>
                <GridDeleteIcon color="error" />
              </Tooltip>
            }
            label={t("common:remove")}
            onClick={() => {
              dispatch(DeleteSection({ formId: formId, sectionId: params?.row?.uuid }));
              setNewMetadataView(
                produce((draft) => {
                  if (
                    selectedMetadataViewGroupsRow === null ||
                    !draft ||
                    !draft.groups ||
                    !draft.groups[selectedMetadataViewGroupsRow] ||
                    !draft.groups[selectedMetadataViewGroupsRow].rows
                  )
                    return;
                  const columnIndex = draft!.groups![selectedMetadataViewGroupsRow!]!.rows![0]!.columns!.findIndex(
                    (x) => x!.metadataFieldIds && x!.metadataFieldIds[0] === (params.id as number)
                  );
                  if (columnIndex < 0) return;
                  draft!.groups![selectedMetadataViewGroupsRow!].rows![0].columns!.splice(columnIndex, 1);

                  draft!.groups = [...draft.groups]; // Trigger updates
                })
              );
            }}
          />
        );
        return actions;
      },
    },
  ];

  const metadataRows = useMemo(() => {
    if (selectedMetadataViewGroupsRow === null) return [];
    if (!newMetadataView.groups![selectedMetadataViewGroupsRow]) return [];

    const result: any[] = [];
    newMetadataView.groups![selectedMetadataViewGroupsRow].rows?.forEach((x) =>
      x.columns?.forEach((c) =>
        c.metadataFieldIds?.forEach((id) => {
          result.push({
            id: id,
            metadataName: cacheService.GetMetadata().find((m) => m.id === id)?.tagName || "UNKNOWN",
            label: cacheService.GetMetadata().find((m) => m.id === id)?.label || "UNKNOWN",
          });
        })
      )
    );

    return result;
  }, [selectedMetadataViewGroupsRow, newMetadataView, newMetadataView.groups]);

  const nameKeyMap = useMemo(() => {
    let nameKeyMap: { key: string; uuid: string }[] = [];
    newMetadataView!.groups?.forEach((g) => {
      g.name &&
        Object.keys(g.name!).forEach((x) => {
          if (x !== "$type") nameKeyMap.push({ key: x, uuid: g.uuid! });
        });
    });
    return nameKeyMap;
  }, [newMetadataView, newMetadataView.groups]);

  const selectedGroupUuid: string | null = useMemo(() => {
    if (selectedMetadataViewGroupsRow === null || !newMetadataView?.groups) return null;
    if (!newMetadataView.groups[selectedMetadataViewGroupsRow]) return null;
    return newMetadataView.groups[selectedMetadataViewGroupsRow].uuid;
  }, [selectedMetadataViewGroupsRow, newMetadataView]);

  const nameKey: string = useMemo(() => {
    if (!selectedGroupUuid) return "";

    return nameKeyMap.find((x) => x.uuid === selectedGroupUuid)?.key || "";
  }, [selectedGroupUuid]);

  const handleAddMetadata = () => {
    if (!selectedMetadata) return;
    const meta = cacheService.GetMetadata().find((x) => x.tagName === selectedMetadata);
    if (!meta) return;

    setNewMetadataView(
      produce((draft) => {
        draft!.groups![selectedMetadataViewGroupsRow!].rows![0].columns!.push(
          new DbMetadataViewGroupColumn({ metadataFieldIds: [meta.id], metadataFields: null })
        );
        draft!.groups = [...draft!.groups!];
      })
    );
    setSelectedMetadata(undefined);
  };

  const handleAddGroup = () => {
    setNewMetadataView(
      produce((draft) => {
        draft!.groups!.push(
          new DbMetadataViewGroup({
            icon: "",
            name: { [nameKey]: "" },
            id: -500,
            rows: [
              new DbMetadataViewGroupRow({
                columns: [],
              }),
            ],
            uuid: uuidv4(),
          })
        );
      })
    );
    setSelectedMetadataViewGroupsRow(newMetadataView.groups!.length);
  };

  const handleDeleteGroup = () => {
    setNewMetadataView(
      produce((draft) => {
        selectedMetadataViewGroupsRow !== null && draft!.groups!.splice(selectedMetadataViewGroupsRow, 1);
      })
    );
    setSelectedMetadataViewGroupsRow((x) => x! - 1);
    dispatch(
      DeleteSection({ formId: formId, sectionId: newMetadataView!.groups![selectedMetadataViewGroupsRow!].uuid! })
    );
  };
  const handleChooseIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNewOrder = (order: string[]) => {
    const newSelectedRow =
      selectedMetadataViewGroupsRow !== null && newMetadataView.groups![selectedMetadataViewGroupsRow]
        ? order.findIndex((x) => x === newMetadataView.groups![selectedMetadataViewGroupsRow]!.uuid)
        : null;
    if (newSelectedRow !== null) setSelectedMetadataViewGroupsRow(newSelectedRow);

    setNewMetadataView(
      produce((draft) => {
        if (draft!.groups) draft!.groups = order.map((x) => ({ ...draft!.groups!.find((g) => g.uuid === x)! }));
      })
    );
  };

  const renderGroupList: ReactElement<any, string | JSXElementConstructor<any>>[] = useMemo(() => {
    const result: ReactElement<any, string | JSXElementConstructor<any>>[] = [];
    newMetadataView.groups!.forEach((x, i) => {
      result.push(
        <Card
          key={x.uuid}
          onClick={() => setSelectedMetadataViewGroupsRow(i)}
          sx={{
            cursor: "pointer",
            border: `solid 1px ${
              x?.rows?.[0].columns?.[0].metadataFieldIds &&
              x.rows[0].columns[0].metadataFieldIds?.length > 0 &&
              nameKeyMap.find((y) => y.uuid === x.uuid) &&
              x.name![nameKeyMap.find((y) => y.uuid === x.uuid)!.key]
                ? selectedMetadataViewGroupsRow === i
                  ? theme.palette.primary.mainHover
                  : theme.palette.divider
                : selectedMetadataViewGroupsRow === i
                ? theme.palette.error.main
                : theme.palette.warning.main
            }`,
            height: "50px",
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
            mt: 1,
            mb: 1,
          }}
        >
          <Grid container>
            <Grid item md={2} xs={2} sm={2}>
              <>
                <Checkbox
                  sx={{
                    color:
                      metadataRows.length > 0 &&
                      nameKeyMap.find((y) => y.uuid === x.uuid) &&
                      x.name![nameKeyMap.find((y) => y.uuid === x.uuid)!.key]
                        ? "inherit"
                        : `${theme.palette.error.main} !important`,
                  }}
                  checked={selectedMetadataViewGroupsRow === i}
                  onClick={() => setSelectedMetadataViewGroupsRow(i)}
                ></Checkbox>
              </>
            </Grid>
            <Grid sx={{ justifyContent: "end", display: "flex", alignItems: "center" }} item md={1} xs={2} sm={1}>
              <Chip
                sx={{ mr: 1.5 }}
                color={
                  x.rows![0]!.columns?.filter((c) => c.metadataFieldIds && c.metadataFieldIds.length > 0).length === 0
                    ? selectedMetadataViewGroupsRow === i
                      ? "error"
                      : "warning"
                    : selectedMetadataViewGroupsRow === i
                    ? "primary"
                    : "default"
                }
                label={`${
                  x.rows![0]!.columns?.filter((c) => c.metadataFieldIds && c.metadataFieldIds.length > 0).length
                }`}
              />
            </Grid>
            <Grid sx={{ display: "flex", alignItems: "center" }} item md={8} xs={6} sm={8}>
              <Typography variant="subtitle2" component="h3" fontWeight={600}>
                {nameKeyMap.find((y) => y.uuid === x.uuid)
                  ? x.name![nameKeyMap.find((y) => y.uuid === x.uuid)!.key] || ""
                  : ""}
              </Typography>
            </Grid>

            <Grid item md={1} xs={2} sm={1}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "end" }} height={"100%"} mr={1.5}>
                <NewMetadataIcon fontSize="medium" icon={x.icon || ""} />
              </Box>
            </Grid>
          </Grid>
        </Card>
      );
    });

    return result;
  }, [newMetadataView, newMetadataView.groups, selectedMetadataViewGroupsRow]);

  return (
    <Grid container spacing={2}>
      <Grid item md={4} xs={12} sm={12}>
        <Stack spacing={2} direction={"column"}>
          {newMetadataView.groups && newMetadataView.groups.length > 0 && (
            <Box width={"100%"} display={"flex"} flexDirection={"column"}>
              <Box display={"flex"} flexDirection={"row"}>
                <Typography mb={1} variant="h5">
                  {t("page:body.groupsOrder")}
                </Typography>
                <Tooltip title={t("tooltip:title.reorderGroupsByDragAndDropping")}>
                  <QuestionMarkIcon
                    sx={{
                      position: "relative",
                      left: "6px",
                      opacity: 0.75,
                      backgroundColor: theme.palette.background.alphaHover,
                      borderRadius: "50%",
                      p: 0.5,
                    }}
                    fontSize="medium"
                  />
                </Tooltip>
              </Box>
              <DragOrderedChildren callback={handleNewOrder} children={renderGroupList} cacheListKey="" />
              <Button onClick={handleAddGroup} sx={{ mt: 1 }} variant="outlined" color="primary">
                {t("button:addGroup")}
              </Button>
            </Box>
          )}
        </Stack>
      </Grid>

      <Grid item md={8} xs={12} sm={12}>
        {selectedMetadataViewGroupsRow !== null &&
          newMetadataView &&
          newMetadataView.groups &&
          newMetadataView.groups[selectedMetadataViewGroupsRow] && (
            <>
              <FormSection
                size="small"
                sx={{ mb: 2 }}
                title={t("form:section.title.baseGroupInfos")}
                icon={<InfoIcon />}
                defaultExpanded={true}
              >
                <Grid spacing={2} container>
                  <Grid item md={6} sm={12} xs={12}>
                    <FormInputText
                      fullWidth
                      value={newMetadataView.groups![selectedMetadataViewGroupsRow].name![nameKey] ?? ""}
                      onChange={(v) =>
                        setNewMetadataView(
                          produce((draft) => {
                            for (const key of Object.keys(draft!.groups![selectedMetadataViewGroupsRow].name!)) {
                              if (key !== "$type") {
                                draft!.groups![selectedMetadataViewGroupsRow].name![key] = v.target.value;
                              }
                            }
                            draft!.groups![selectedMetadataViewGroupsRow].name![nameKey] = v.target.value;
                            draft!.groups = [...draft!.groups!];
                          })
                        )
                      }
                      validationProps={{
                        formId: formId,
                        formSectionId: newMetadataView.groups[selectedMetadataViewGroupsRow].uuid!,
                        fieldId: `viewGroupName-${newMetadataView.groups[selectedMetadataViewGroupsRow].uuid}`,
                        required: true,
                        initialValue:
                          newMetadataView.groups![selectedMetadataViewGroupsRow].name![nameKey] || undefined,
                      }}
                      stackProps={{ width: "100%" }}
                      label={t("common:name")}
                    />
                  </Grid>

                  <Grid sx={{ position: "relative" }} item md={2} sm={6} xs={6}>
                    <FormInputText
                      fullWidth
                      value={newMetadataView.groups![selectedMetadataViewGroupsRow].icon ?? ""}
                      onChange={(v) =>
                        setNewMetadataView(
                          produce((draft) => {
                            draft!.groups![selectedMetadataViewGroupsRow].icon = v.target.value;
                            draft!.groups = [...draft!.groups!];
                          })
                        )
                      }
                      placeholder={t("field:placeholder.defaultIcon")}
                      validationProps={{
                        formId: formId,
                        formSectionId: newMetadataView.groups[selectedMetadataViewGroupsRow].uuid!,
                        fieldId: `metaViewIcon-${newMetadataView.groups[selectedMetadataViewGroupsRow].uuid}`,
                        required: false,
                        initialValue: newMetadataView.groups![selectedMetadataViewGroupsRow].icon || undefined,
                      }}
                      stackProps={{ width: "100%" }}
                      label={t("common:icon")}
                    />
                    <Tooltip title={t("tooltip:title.chooseIconTitle")}>
                      <QuestionMarkIcon
                        sx={{
                          position: "absolute",
                          left: "50px",
                          top: "12px",
                          opacity: 0.75,
                          backgroundColor: theme.palette.background.alphaHover,
                          borderRadius: "50%",
                          p: 0.5,
                        }}
                        fontSize="medium"
                      />
                    </Tooltip>
                  </Grid>

                  <Grid sx={{ display: "flex", alignItems: "start" }} item md={2} sm={3} xs={3}>
                    <Button
                      sx={{ mt: 3.5, whiteSpace: "nowrap", textAlign: "center" }}
                      fullWidth
                      variant="outlined"
                      color="primary"
                      endIcon={<AppsIcon fontSize="large" />}
                      aria-describedby={id}
                      onClick={handleChooseIconClick}
                    >
                      {t("button:selectIcon")}
                    </Button>
                    <Popover
                      sx={{ opacity: 0.9 }}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      id={id}
                      open={open}
                      anchorEl={anchorEl}
                      onClose={handleClose}
                    >
                      <Stack
                        mt={1}
                        mb={1}
                        width={"400px"}
                        maxHeight={"300px"}
                        direction={"column"}
                        alignItems={"center"}
                        spacing={2}
                      >
                        <Stack
                          overflow={"scroll"}
                          direction={"row"}
                          flexWrap={"wrap"}
                          spacing={2}
                          rowGap={2}
                          display={"flex"}
                          sx={(theme) => ({ ...theme.helpers(theme).scrollBar })}
                        >
                          {SELECTABLE_ICON_LIST.filter(
                            (x) => filterIcon === "" || x.includes(filterIcon.toLocaleLowerCase())
                          ).map((x, i) => (
                            <Tooltip title={x}>
                              <IconButton
                                color={
                                  newMetadataView.groups![selectedMetadataViewGroupsRow].icon &&
                                  newMetadataView.groups![selectedMetadataViewGroupsRow].icon === x
                                    ? "primary"
                                    : "inherit"
                                }
                                sx={{
                                  ml: 2,
                                  width: "48px",
                                  height: "48px",
                                  p: 1,
                                  backgroundColor: theme.palette.background.alphaHover,
                                }}
                                onClick={() => {
                                  setNewMetadataView(
                                    produce((draft) => {
                                      draft!.groups![selectedMetadataViewGroupsRow].icon = x;
                                      draft!.groups = [...draft!.groups!]; // Trigger updates
                                    })
                                  );
                                }}
                              >
                                <NewMetadataIcon icon={x} />
                              </IconButton>
                            </Tooltip>
                          ))}
                        </Stack>
                        <OutlinedInput
                          sx={{ height: "24px", width: "376px", mt: "0px !important" }}
                          value={filterIcon}
                          placeholder={t("field:placeholder.filterIconList")}
                          onChange={(e) => setFilterIcon(e.target.value)}
                        />
                      </Stack>
                    </Popover>
                  </Grid>
                  <Grid sx={{ display: "flex", alignItems: "start" }} item md={2} sm={3} xs={3}>
                    <Button
                      sx={{ mt: 3.5, whiteSpace: "nowrap", textAlign: "center" }}
                      target="_blank"
                      href="https://mui.com/material-ui/material-icons/"
                      LinkComponent={Link}
                      fullWidth
                      variant="outlined"
                    >
                      {t("button:muiIcons")}
                    </Button>
                  </Grid>
                </Grid>
              </FormSection>
              <FormSection
                size="small"
                title={t("form:section.title.metadataFieldsToShow")}
                icon={<InfoIcon />}
                defaultExpanded={true}
              >
                {metadataRows.length > 0 && (
                  <DataTable
                    noControls
                    disableRowSelectionOnClick
                    rowHeight={32}
                    pageSize={100}
                    columnHeaderHeight={32}
                    rows={metadataRows}
                    columns={metadataCols}
                  />
                )}
                <Stack mt={2} display={"flex"} direction={"row"} width={"100%"} justifyContent={"space-between"}>
                  <Box m={0} p={0} height={"100%"} display={"flex"} flexDirection={"row"} width={"100%"}>
                    <Autocomplete
                      sx={{ width: "300px", mr: 2, mt: -3 }}
                      renderInput={(params) => (
                        <TextField placeholder={t("field:placeholder.selectMetadata")} {...params} />
                      )}
                      value={selectedMetadata ?? ""}
                      onChange={(e, v) => v && setSelectedMetadata(v)}
                      options={cacheService
                        .GetMetadata()
                        .filter((x) => metadataRows.find((m) => m.id === x.id) === undefined)
                        .map((m) => m.tagName)}
                      filterOptions={(x, s) => {
                        let match = cacheService
                          .GetMetadata()
                          .filter((x) => metadataRows.find((m) => m.id === x.id) === undefined)
                          .map((m) => m.tagName)
                          .filter((x) => x?.toLowerCase().includes(s.inputValue.toLowerCase()));
                        return x.filter((a) => a && match.includes(a));
                      }}
                    />
                    <Button
                      disabled={!selectedMetadata}
                      onClick={handleAddMetadata}
                      sx={{ width: "150px" }}
                      variant="outlined"
                      color="primary"
                    >
                      {t("button:addMetadata")}
                    </Button>
                  </Box>
                  <Button
                    disabled={newMetadataView!.groups!.length <= 1}
                    onClick={handleDeleteGroup}
                    sx={{ width: "150px" }}
                    variant="contained"
                    color="error"
                  >
                    {t("button:deleteGroup")}
                  </Button>
                </Stack>
              </FormSection>
            </>
          )}
      </Grid>
    </Grid>
  );
};

export default EditMetadataViewGroups;
